import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, CheckCircle2, XCircle, Clock, Droplets, Activity, ShieldCheck, Zap, AlertTriangle, Lock, XIcon, Star, MousePointerClick, Mail, Sun, Play } from 'lucide-react';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.7,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
const CTAButton = ({
  className = '',
  href = '#checkout',
  onClick
}: {
  className?: string;
  href?: string;
  onClick?: () => void;
}) => <div className={`flex flex-col items-center ${className}`} data-id="element-1">
    <a href={href} onClick={onClick} className="group relative inline-flex items-center justify-center gap-3 bg-yellow text-dark font-bold text-lg md:text-xl px-8 py-5 rounded-sm uppercase tracking-wider hover:bg-[#ccff00] transition-all duration-300 shadow-[0_0_30px_rgba(229,255,0,0.15)] hover:shadow-[0_0_40px_rgba(229,255,0,0.3)] w-full md:w-auto overflow-hidden" data-id="element-2">
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" data-id="element-3" />
      <span data-id="element-4">Get The 24-Hour Gut Flush Now</span>
      <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-5" />
    </a>
    <div className="flex items-center gap-2 mt-3 text-sm text-neutral-400" data-id="element-6">
      <Lock className="w-4 h-4" data-id="element-7" />
      <span data-id="element-8">Secure Checkout - Immediate PDF Access</span>
    </div>
  </div>;
const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState(2 * 3600 + 47 * 60 + 33);
  useEffect(() => {
    const saved = sessionStorage.getItem('gut_flush_timer');
    if (saved) {
      setTimeLeft(parseInt(saved, 10));
    }
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        const next = prev > 0 ? prev - 1 : 0;
        sessionStorage.setItem('gut_flush_timer', next.toString());
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const h = Math.floor(timeLeft / 3600).toString().padStart(2, '0');
  const m = Math.floor(timeLeft % 3600 / 60).toString().padStart(2, '0');
  const s = (timeLeft % 60).toString().padStart(2, '0');
  return <div className="flex flex-col items-center mb-8" data-id="element-9">
      <span className="text-red-500 font-bold uppercase tracking-widest text-sm mb-3" data-id="element-10">
        This price expires in:
      </span>
      <div className="flex gap-3 text-4xl font-serif text-white" data-id="element-11">
        <div className="bg-neutral-900 px-4 py-3 rounded-xl border border-red-500/30 shadow-[0_0_15px_rgba(239,68,68,0.15)]" data-id="element-12">
          {h}
        </div>
        <span className="self-center text-red-500/50" data-id="element-13">:</span>
        <div className="bg-neutral-900 px-4 py-3 rounded-xl border border-red-500/30 shadow-[0_0_15px_rgba(239,68,68,0.15)]" data-id="element-14">
          {m}
        </div>
        <span className="self-center text-red-500/50" data-id="element-15">:</span>
        <div className="bg-neutral-900 px-4 py-3 rounded-xl border border-red-500/30 shadow-[0_0_15px_rgba(239,68,68,0.15)]" data-id="element-16">
          {s}
        </div>
      </div>
    </div>;
};
const LegalModal = ({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => <AnimatePresence data-id="element-17">
    {isOpen && <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-18">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-19" />
        <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.3
    }} className="relative bg-neutral-900 border border-neutral-700 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-20">
          <div className="flex items-center justify-between p-6 border-b border-neutral-800" data-id="element-21">
            <h3 className="font-serif text-2xl text-white" data-id="element-22">{title}</h3>
            <button onClick={onClose} className="text-neutral-400 hover:text-white transition-colors p-1" data-id="element-23">
              <XIcon className="w-6 h-6" data-id="element-24" />
            </button>
          </div>
          <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)] text-neutral-300 leading-relaxed space-y-4 text-sm" data-id="element-25">
            {children}
          </div>
        </motion.div>
      </motion.div>}
  </AnimatePresence>;
// 1. SOCIAL PROOF TOAST
const SocialProofToast = () => {
  const [currentToast, setCurrentToast] = useState(-1);
  const [isVisible, setIsVisible] = useState(false);
  const toasts = ['Emily R. from Nashville, TN just purchased — 2 min ago', 'David K. from Portland, OR just purchased — 5 min ago', 'Maria S. from Phoenix, AZ just purchased — 8 min ago', 'Chris W. from Atlanta, GA just purchased — 11 min ago', 'Jennifer L. from San Diego, CA just purchased — 14 min ago'];
  useEffect(() => {
    const showNextToast = () => {
      setCurrentToast(prev => (prev + 1) % toasts.length);
      setIsVisible(true);
      setTimeout(() => {
        setIsVisible(false);
      }, 4000); // Hide after 4s
    };
    // Initial delay before first toast
    const initialTimer = setTimeout(showNextToast, 5000);
    // Cycle every 25 seconds
    const interval = setInterval(showNextToast, 25000);
    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, []);
  return <AnimatePresence data-id="element-26">
      {isVisible && currentToast >= 0 && <motion.div initial={{
      opacity: 0,
      x: -50,
      y: 20
    }} animate={{
      opacity: 1,
      x: 0,
      y: 0
    }} exit={{
      opacity: 0,
      x: -50,
      scale: 0.9
    }} className="fixed bottom-6 left-6 z-50 bg-neutral-900 border border-neutral-700 shadow-2xl rounded-lg p-4 pr-10 max-w-xs" data-id="element-27">
          <div className="flex items-start gap-3" data-id="element-28">
            <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5 animate-pulse shrink-0" data-id="element-29" />
            <p className="text-sm text-neutral-200 leading-snug" data-id="element-30">
              {toasts[currentToast]}
            </p>
          </div>
          <button onClick={() => setIsVisible(false)} className="absolute top-2 right-2 text-neutral-500 hover:text-white" data-id="element-31">
            <XIcon className="w-4 h-4" data-id="element-32" />
          </button>
        </motion.div>}
    </AnimatePresence>;
};
// 3. EXIT INTENT POPUP
const ExitIntentPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    const handleMouseOut = (e: MouseEvent) => {
      if (e.clientY < 0 && !sessionStorage.getItem('exit_intent_shown')) {
        setIsOpen(true);
        sessionStorage.setItem('exit_intent_shown', 'true');
      }
    };
    document.addEventListener('mouseout', handleMouseOut);
    return () => document.removeEventListener('mouseout', handleMouseOut);
  }, []);
  return <AnimatePresence data-id="element-33">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-34">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setIsOpen(false)} data-id="element-35" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} className="relative bg-neutral-900 border-2 border-red-500/30 rounded-3xl max-w-lg w-full p-8 text-center shadow-[0_0_50px_rgba(239,68,68,0.15)]" data-id="element-36">
            <button onClick={() => setIsOpen(false)} className="absolute top-4 right-4 text-neutral-500 hover:text-white" data-id="element-37">
              <XIcon className="w-6 h-6" data-id="element-38" />
            </button>

            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-39">
              <AlertTriangle className="w-8 h-8 text-red-500" data-id="element-40" />
            </div>

            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4" data-id="element-41">
              Wait — Don't Leave Bloated!
            </h2>
            <p className="text-neutral-300 text-lg mb-8" data-id="element-42">
              You're seconds away from the method that has helped{' '}
              <strong className="text-white" data-id="element-43">2,847+ people</strong> flatten
              their stomach in 24 hours.
            </p>

            <div className="bg-dark rounded-xl p-6 mb-8 border border-neutral-800" data-id="element-44">
              <p className="text-neutral-500 line-through text-xl mb-1" data-id="element-45">$129</p>
              <p className="text-5xl font-serif text-white mb-2" data-id="element-46">
                $12<span className="text-3xl" data-id="element-47">.99</span>
              </p>
              <p className="text-red-500 font-bold text-sm uppercase tracking-wider" data-id="element-48">
                Last Chance Offer
              </p>
            </div>

            <a href="#checkout" onClick={() => setIsOpen(false)} className="inline-flex items-center justify-center gap-2 bg-yellow text-dark font-bold text-lg px-8 py-4 rounded-sm uppercase tracking-wider hover:bg-[#ccff00] transition-all duration-300 w-full" data-id="element-49">
              Get It Now Before It Expires
            </a>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
};
// 4. STICKY MOBILE BAR
const StickyMobileBar = () => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      // Show after scrolling past hero (approx 600px)
      setIsVisible(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <AnimatePresence data-id="element-50">
      {isVisible && <motion.div initial={{
      y: 100
    }} animate={{
      y: 0
    }} exit={{
      y: 100
    }} className="fixed bottom-0 left-0 w-full z-40 md:hidden bg-dark/95 backdrop-blur border-t border-neutral-800 p-4 pb-safe" data-id="element-51">
          <div className="flex items-center justify-between gap-4" data-id="element-52">
            <div className="flex flex-col" data-id="element-53">
              <span className="text-neutral-500 text-xs line-through" data-id="element-54">
                $129
              </span>
              <span className="text-white font-serif text-2xl leading-none" data-id="element-55">
                $12.99
              </span>
            </div>
            <a href="#checkout" className="bg-yellow text-dark font-bold px-6 py-3 rounded-sm uppercase tracking-wider text-sm flex-1 text-center shadow-[0_0_15px_rgba(229,255,0,0.2)]" data-id="element-56">
              Get It Now →
            </a>
          </div>
        </motion.div>}
    </AnimatePresence>;
};
// Meta Pixel helper
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
  }
}
const useMetaPixel = () => {
  useEffect(() => {
    // Load Meta Pixel script
    const f = window as any;
    if (f.fbq) return;
    const n: any = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const t = document.createElement('script');
    t.async = true;
    t.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(t);
    window.fbq('init', '1154453463472983');
    window.fbq('track', 'PageView');
  }, []);
};
const fireAddToCart = () => {
  if (window.fbq) {
    window.fbq('track', 'AddToCart', {
      content_name: 'The 24-Hour Gut Flush',
      content_type: 'product',
      value: 12.99,
      currency: 'USD'
    });
  }
};
export function App() {
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  useMetaPixel();
  return <div className="min-h-screen bg-dark font-sans selection:bg-cyan selection:text-dark" data-id="element-57">
      <SocialProofToast data-id="element-58" />
      <ExitIntentPopup data-id="element-59" />
      <StickyMobileBar data-id="element-60" />

      {/* ═══════════════════════════════════════════
                                1. HERO — Hook + CTA above the fold
                            ═══════════════════════════════════════════ */}
      <section className="relative pt-12 pb-16 md:pt-24 md:pb-24 overflow-hidden border-b border-neutral-800" data-id="element-61">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-cyan/10 blur-[120px] rounded-full pointer-events-none" data-id="element-62" />
        <div className="container mx-auto px-6 max-w-6xl relative z-10" data-id="element-63">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-64">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-65">
              <div className="inline-block border border-cyan/30 bg-cyan/10 text-cyan px-4 py-1.5 rounded-full text-sm font-semibold tracking-wide mb-6" data-id="element-66">
                THE RAPID OSMOTIC RESET
              </div>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.1] text-white mb-6" data-id="element-67">
                The 24-Hour Gut Flush:{' '}
                <span className="text-cyan" data-id="element-68">The Science-Backed Ritual</span> to
                End Bloat by Tomorrow Morning.
              </h1>
              <p className="text-xl md:text-2xl text-neutral-300 leading-relaxed mb-10" data-id="element-69">
                Stop the "Pants-Tension" and flatten your stomach using the
                Rapid Osmotic Reset—without laxatives, restrictive dieting, or
                useless detox teas.
              </p>
              <CTAButton className="items-start" data-id="element-70" />
            </motion.div>

            {/* 5. VIDEO PLACEHOLDER IN HERO */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="relative" data-id="element-71">
              <div className="absolute -inset-4 bg-cyan/20 rounded-3xl blur-3xl animate-pulse" data-id="element-72" />
              <div className="absolute -inset-1 bg-gradient-to-tr from-cyan/30 via-yellow/10 to-cyan/20 rounded-2xl blur-xl" data-id="element-73" />

              <a href="#checkout" className="group relative block z-10 w-full h-auto rounded-2xl shadow-[0_0_60px_rgba(0,229,204,0.25)] border-2 border-cyan/30 overflow-hidden cursor-pointer" data-id="element-74">
                <img src="https://cdn.magicpatterns.com/uploads/pmDY3YFeFRMaNMgmFcGXCS/the_24_hours.png" alt="24-Hour Gut Flush on Mobile Device" className="w-full h-auto brightness-90 contrast-105 group-hover:scale-105 transition-transform duration-700" data-id="element-75" />
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors" data-id="element-76" />

                <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-77">
                  <div className="w-20 h-20 bg-cyan/90 rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(0,229,204,0.6)] animate-[pulse_2s_infinite] group-hover:scale-110 transition-transform" data-id="element-78">
                    <Play className="w-8 h-8 text-dark fill-dark ml-1" data-id="element-79" />
                  </div>
                  <div className="mt-6 bg-dark/80 backdrop-blur-sm px-6 py-2 rounded-full border border-cyan/30" data-id="element-80">
                    <span className="text-white font-bold tracking-wide" data-id="element-81">
                      Watch the 3-Minute Breakdown ▶
                    </span>
                  </div>
                </div>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
                                2. CREDIBILITY BAR — Instant trust
                            ═══════════════════════════════════════════ */}
      <div className="bg-neutral-900 border-b border-neutral-800 py-5" data-id="element-82">
        <div className="container mx-auto px-6 max-w-6xl flex flex-wrap justify-center gap-6 md:gap-12 text-sm md:text-base text-cyan font-medium" data-id="element-83">
          <span className="flex items-center gap-2" data-id="element-84">
            <CheckCircle2 className="w-5 h-5" data-id="element-85" /> Based on Peer-Reviewed
            Electrolyte Research
          </span>
          <span className="flex items-center gap-2" data-id="element-86">
            <CheckCircle2 className="w-5 h-5" data-id="element-87" /> 2,847+ Customers Served
          </span>
          <span className="flex items-center gap-2" data-id="element-88">
            <CheckCircle2 className="w-5 h-5" data-id="element-89" /> Featured in Digestive Health
            Weekly
          </span>
        </div>
      </div>

      {/* ═══════════════════════════════════════════
                                3. EMOTIONAL HOOK + IMAGINE (merged)
                                — Pain identification → future pacing
                            ═══════════════════════════════════════════ */}
      <section className="py-20 bg-neutral-950" data-id="element-90">
        <div className="container mx-auto px-6 max-w-3xl" data-id="element-91">
          <FadeIn data-id="element-92">
            <p className="text-2xl md:text-3xl font-serif leading-relaxed text-white mb-8" data-id="element-93">
              <span className="text-cyan" data-id="element-94">It's 7:15 AM.</span> You're standing
              in front of the mirror, and the pants that fit perfectly two days
              ago refuse to button.
            </p>
            <p className="text-lg text-neutral-300 mb-6 leading-relaxed" data-id="element-95">
              You feel heavy, tight, and physically miserable. It's not fat—it's
              stagnation.
            </p>
            <p className="text-lg text-neutral-300 mb-12 leading-relaxed" data-id="element-96">
              You don't have thirty days to wait for a "cleanse" to kick in. You
              have a meeting, a flight, or a social event tomorrow, and you need
              your body to cooperate{' '}
              <strong className="text-white" data-id="element-97">right now.</strong>
            </p>
            <div className="border border-yellow/30 bg-yellow/5 p-8 md:p-10 rounded-2xl text-center" data-id="element-98">
              <p className="font-serif text-2xl md:text-3xl text-white leading-snug" data-id="element-99">
                Now imagine that{' '}
                <span className="text-yellow" data-id="element-100">24 hours from now</span>, that
                tightness, that discomfort…{' '}
                <span className="text-yellow" data-id="element-101">gone.</span>
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
                                4. THE PROBLEM — Reframe the issue
                            ═══════════════════════════════════════════ */}
      <section className="py-20 border-y border-neutral-800 bg-dark" data-id="element-102">
        <div className="container mx-auto px-6 max-w-4xl" data-id="element-103">
          <FadeIn data-id="element-104">
            <h2 className="font-serif text-4xl md:text-5xl text-center mb-12" data-id="element-105">
              Most people treat bloating like a weight problem, but it's
              actually a{' '}
              <span className="text-cyan border-b-2 border-cyan" data-id="element-106">
                pressure problem.
              </span>
            </h2>
            <div className="bg-neutral-900/50 border border-neutral-800 p-8 md:p-10 rounded-2xl text-lg text-neutral-300 leading-relaxed space-y-6" data-id="element-107">
              <p data-id="element-108">
                Your body is hoarding water in your interstitial tissues because
                it's stuck in an inflammatory loop.
              </p>
              <p data-id="element-109">
                When you feel that "6-month pregnant" food baby after a meal,
                your internal "cleaning broom"—the{' '}
                <strong className="text-white" data-id="element-110">Migrating Motor Complex</strong>
                —has officially stalled.
              </p>
              <p data-id="element-111">
                Drinking more plain water or eating "cleansing" fiber only adds
                more weight to a system that isn't moving.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
                                5. FAILED SOLUTIONS — Agitate
                            ═══════════════════════════════════════════ */}
      <section className="py-24 bg-neutral-950" data-id="element-112">
        <div className="container mx-auto px-6 max-w-5xl" data-id="element-113">
          <FadeIn data-id="element-114">
            <h2 className="font-serif text-4xl md:text-5xl text-center mb-16" data-id="element-115">
              Why What You Tried Didn't Work
            </h2>
          </FadeIn>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-116">
            {[{
            title: 'Laxative Teas',
            desc: "Irritate the gut lining and cause a massive 'rebound bloat' 24 hours later."
          }, {
            title: 'Plain Water Flushes',
            desc: 'Dilute your electrolytes, signaling your cells to hold onto water even tighter.'
          }, {
            title: "High-Fiber 'Cleanses'",
            desc: 'Create more gas and fermentation in a stalled digestive tract.'
          }, {
            title: 'Iced Drinks',
            desc: "Shock your digestive enzymes and stop the 'gut broom' from sweeping."
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-117">
                <div className="bg-dark border border-neutral-800 p-8 rounded-xl flex gap-4 h-full" data-id="element-118">
                  <XCircle className="w-8 h-8 text-red-500 shrink-0" data-id="element-119" />
                  <div data-id="element-120">
                    <h3 className="text-xl font-bold text-white mb-2" data-id="element-121">
                      {item.title}
                    </h3>
                    <p className="text-neutral-400" data-id="element-122">{item.desc}</p>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
                                NEW: PRICE COMPARISON SECTION
                            ═══════════════════════════════════════════ */}
      <section className="py-24 bg-dark border-y border-neutral-800" data-id="element-123">
        <div className="container mx-auto px-6 max-w-4xl" data-id="element-124">
          <FadeIn data-id="element-125">
            <h2 className="font-serif text-4xl md:text-5xl text-center mb-16" data-id="element-126">
              The Real Cost of Staying Bloated
            </h2>

            <div className="space-y-4" data-id="element-127">
              {[{
              title: 'Gastroenterologist visit',
              price: '$250-$400'
            }, {
              title: 'Monthly probiotics subscription',
              price: '$45/month'
            }, {
              title: "Detox teas that don't work",
              price: '$30-$60'
            }, {
              title: 'Compression garments (hiding, not fixing)',
              price: '$35-$80'
            }].map((item, i) => <div key={i} className="flex items-center justify-between bg-neutral-900 border border-neutral-800 p-6 rounded-xl" data-id="element-128">
                  <span className="text-neutral-400 text-lg" data-id="element-129">{item.title}</span>
                  <span className="text-neutral-500 font-serif text-xl" data-id="element-130">
                    {item.price}
                  </span>
                </div>)}

              <div className="relative mt-8" data-id="element-131">
                <div className="absolute -inset-1 bg-gradient-to-r from-cyan via-yellow to-cyan rounded-xl blur opacity-30 animate-pulse" data-id="element-132" />
                <div className="relative flex items-center justify-between bg-dark border-2 border-cyan p-6 md:p-8 rounded-xl shadow-[0_0_30px_rgba(0,229,204,0.15)]" data-id="element-133">
                  <div className="flex items-center gap-4" data-id="element-134">
                    <div className="bg-cyan/20 p-2 rounded-full" data-id="element-135">
                      <CheckCircle2 className="w-6 h-6 text-cyan" data-id="element-136" />
                    </div>
                    <span className="text-white font-bold text-xl md:text-2xl" data-id="element-137">
                      The 24-Hour Gut Flush
                    </span>
                  </div>
                  <span className="text-yellow font-serif text-3xl md:text-4xl font-bold" data-id="element-138">
                    $12.99
                  </span>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
                                6. THE UNIQUE MECHANISM — The solution
                            ═══════════════════════════════════════════ */}
      <section className="py-24 relative bg-neutral-950" data-id="element-139">
        <div className="absolute inset-0 bg-cyan/5" data-id="element-140" />
        <div className="container mx-auto px-6 max-w-4xl relative z-10" data-id="element-141">
          <FadeIn data-id="element-142">
            <div className="border-2 border-cyan/30 bg-dark/80 backdrop-blur-sm p-10 md:p-14 rounded-3xl shadow-[0_0_50px_rgba(0,229,204,0.1)]" data-id="element-143">
              <div className="flex items-center justify-center mb-8" data-id="element-144">
                <Zap className="w-12 h-12 text-cyan" data-id="element-145" />
              </div>
              <h2 className="font-serif text-4xl md:text-5xl text-center mb-8" data-id="element-146">
                The Unique Mechanism
              </h2>
              <p className="text-xl text-neutral-300 leading-relaxed text-center mb-8" data-id="element-147">
                The Rapid Osmotic Reset (ROR) focuses on shifting the mineral
                pressure in your cells.
              </p>
              <p className="text-lg text-neutral-400 leading-relaxed text-center" data-id="element-148">
                By using a specific{' '}
                <strong className="text-white" data-id="element-149">Mineral-Matrix Stack</strong>{' '}
                (Sodium + Potassium Bitartrate + Vitamin C), we physically draw
                trapped fluid out of your tissues and back into the digestive
                tract for elimination. We aren't forcing your body; we are
                changing the internal tide.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
                                7. VALUE STACK — What you get + CTA
                            ═══════════════════════════════════════════ */}
      <section className="py-24 bg-dark" data-id="element-150">
        <div className="container mx-auto px-6 max-w-5xl" data-id="element-151">
          <FadeIn data-id="element-152">
            <h2 className="font-serif text-4xl md:text-5xl text-center mb-16" data-id="element-153">
              Everything You Get Today
            </h2>
          </FadeIn>
          <div className="space-y-4 max-w-3xl mx-auto" data-id="element-154">
            {[{
            title: 'The 24-Hour Hour-by-Hour Execution Map',
            desc: 'Exactly what to do from 7 AM to 10 PM.',
            value: '$47'
          }, {
            title: 'The Mineral-Matrix Stack Recipe',
            desc: 'The precise 4-ingredient concentrate that triggers the flush.',
            value: '$37'
          }, {
            title: 'The Gravity-Shift Move',
            desc: 'A 10-minute physical method to assist lymphatic drainage.',
            value: '$27'
          }, {
            title: 'The Forbidden Five List',
            desc: "The 5 specific 'healthy' foods you must avoid to prevent stalling.",
            value: '$17'
          }, {
            title: 'The 24-Hour Success Tracker Checklist',
            desc: 'Keep yourself accountable and on track.',
            value: 'FREE BONUS'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-155">
                <div className="bg-neutral-900 border border-neutral-800 p-6 rounded-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4 hover:border-cyan/50 transition-colors" data-id="element-156">
                  <div className="flex items-start gap-4" data-id="element-157">
                    <CheckCircle2 className="w-6 h-6 text-cyan shrink-0 mt-1" data-id="element-158" />
                    <div data-id="element-159">
                      <h3 className="text-lg font-bold text-white mb-1" data-id="element-160">
                        {item.title}
                      </h3>
                      <p className="text-neutral-400 text-sm md:text-base" data-id="element-161">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                  <div className="md:text-right shrink-0 ml-10 md:ml-0" data-id="element-162">
                    {item.value === 'FREE BONUS' ? <span className="bg-yellow text-dark font-bold px-3 py-1 rounded-full text-sm uppercase tracking-wider" data-id="element-163">
                        {item.value}
                      </span> : <div className="flex flex-col" data-id="element-164">
                        <span className="text-neutral-500 text-sm uppercase tracking-wider" data-id="element-165">
                          Value
                        </span>
                        <span className="text-white font-serif text-xl line-through decoration-red-500/50" data-id="element-166">
                          {item.value}
                        </span>
                      </div>}
                  </div>
                </div>
              </FadeIn>)}
            <FadeIn delay={0.6} data-id="element-167">
              <div className="mt-8 p-6 border-2 border-dashed border-neutral-700 rounded-xl text-center bg-neutral-900/50" data-id="element-168">
                <p className="text-neutral-400 uppercase tracking-widest text-sm mb-2" data-id="element-169">
                  Total Value: $129
                </p>
                <p className="text-3xl font-serif text-white" data-id="element-170">
                  Today Only: <span className="text-cyan" data-id="element-171">$12.99</span>
                </p>
              </div>
            </FadeIn>
          </div>
          <div className="mt-12 flex justify-center" data-id="element-172">
            <FadeIn data-id="element-173">
              <CTAButton data-id="element-174" />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
                                8. HOW IT WORKS — Simple 3 steps
                            ═══════════════════════════════════════════ */}
      <section className="py-24 bg-neutral-950" data-id="element-175">
        <div className="container mx-auto px-6 max-w-5xl" data-id="element-176">
          <FadeIn data-id="element-177">
            <h2 className="font-serif text-4xl md:text-5xl text-center mb-16" data-id="element-178">
              How It Works
            </h2>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-179">
            {[{
            icon: Droplets,
            title: 'Reset the Minerals',
            desc: 'Drink the Mineral-Matrix Stack to signal your cells to release stored water.'
          }, {
            icon: Activity,
            title: 'Activate the Broom',
            desc: "Use warm, transit-first nutrition to restart your gut's internal cleaning waves."
          }, {
            icon: Clock,
            title: 'Flush the Stagnation',
            desc: 'Follow the 3 PM lymphatic drainage shift and the 9 PM cortisol-control sleep prep.'
          }].map((step, i) => <FadeIn key={i} delay={i * 0.2} data-id="element-180">
                <div className="bg-dark border border-neutral-800 p-8 rounded-2xl text-center h-full relative overflow-hidden group hover:border-cyan/30 transition-colors" data-id="element-181">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan to-transparent opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-182" />
                  <div className="w-16 h-16 mx-auto bg-cyan/10 rounded-full flex items-center justify-center mb-6 text-cyan" data-id="element-183">
                    <step.icon className="w-8 h-8" data-id="element-184" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4" data-id="element-185">
                    Step {i + 1}: {step.title}
                  </h3>
                  <p className="text-neutral-400 leading-relaxed" data-id="element-186">
                    {step.desc}
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
                                9. TESTIMONIALS + CTA — Social proof
                            ═══════════════════════════════════════════ */}
      <section className="py-24 bg-dark" data-id="element-187">
        <div className="container mx-auto px-6 max-w-6xl" data-id="element-188">
          <FadeIn data-id="element-189">
            <h2 className="font-serif text-4xl md:text-5xl text-center mb-4" data-id="element-190">
              Real Results From Real People
            </h2>
            <p className="text-center text-neutral-400 text-lg mb-16 max-w-2xl mx-auto" data-id="element-191">
              Within 12 hours, most users report a noticeable "lightness" and a
              measurable decrease in waistline tension — usually 0.5 to 1.5
              inches.
            </p>
          </FadeIn>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-192">
            {[{
            name: 'Sarah M.',
            location: 'Austin, TX',
            text: 'Lost 1.2 inches off my waist in 14 hours. I could button my dress pants for my Monday presentation. This is now my go-to before any big event.'
          }, {
            name: 'James R.',
            location: 'Denver, CO',
            text: 'I was skeptical but tried it before a wedding. Woke up feeling 5 pounds lighter. My wife noticed immediately. Worth every penny.'
          }, {
            name: 'Dr. Lisa K.',
            location: 'Miami, FL',
            text: 'As a nurse practitioner, I was impressed by the mineral science behind this. I now recommend it to patients dealing with acute bloating episodes.'
          }, {
            name: 'Mike T.',
            location: 'Chicago, IL',
            text: 'Tried every detox tea on Amazon. This was the first thing that actually worked overnight. No cramps, no bathroom emergencies. Just results.'
          }].map((t, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-193">
                <div className="bg-neutral-900 border border-neutral-800 p-8 rounded-2xl h-full flex flex-col" data-id="element-194">
                  <div className="flex gap-1 mb-4" data-id="element-195">
                    {[...Array(5)].map((_, idx) => <Star key={idx} className="w-5 h-5 fill-yellow text-yellow" data-id="element-196" />)}
                  </div>
                  <p className="text-neutral-300 text-lg italic mb-6 grow" data-id="element-197">
                    "{t.text}"
                  </p>
                  <div className="flex items-center gap-3 mt-auto" data-id="element-198">
                    <div className="w-10 h-10 rounded-full bg-neutral-800 flex items-center justify-center text-cyan font-bold text-sm" data-id="element-199">
                      {t.name.charAt(0)}
                    </div>
                    <div data-id="element-200">
                      <p className="text-white font-bold" data-id="element-201">{t.name}</p>
                      <p className="text-neutral-500 text-sm" data-id="element-202">{t.location}</p>
                    </div>
                  </div>
                </div>
              </FadeIn>)}
          </div>
          <div className="mt-12 flex justify-center" data-id="element-203">
            <FadeIn data-id="element-204">
              <CTAButton data-id="element-205" />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
                                10. WHO THIS IS FOR — Qualification
                            ═══════════════════════════════════════════ */}
      <section className="py-24 border-y border-neutral-800 bg-neutral-950" data-id="element-206">
        <div className="container mx-auto px-6 max-w-6xl" data-id="element-207">
          <div className="grid md:grid-cols-2 gap-12" data-id="element-208">
            <FadeIn data-id="element-209">
              <h3 className="font-serif text-3xl mb-8 flex items-center gap-3" data-id="element-210">
                <CheckCircle2 className="w-8 h-8 text-cyan" data-id="element-211" /> Who This Is For
              </h3>
              <ul className="space-y-6" data-id="element-212">
                {['Professionals who need to look and feel sharp for high-stakes meetings.', "People with 'Evening Bloat' that makes social outings uncomfortable.", "Anyone who feels 'tight' and heavy despite eating a healthy diet.", 'Travelers dealing with post-flight water retention and digestive stall.'].map((text, i) => <li key={i} className="flex gap-4 text-neutral-300 text-lg" data-id="element-213">
                    <div className="w-2 h-2 rounded-full bg-cyan mt-2.5 shrink-0" data-id="element-214" />
                    {text}
                  </li>)}
              </ul>
            </FadeIn>
            <FadeIn delay={0.2} data-id="element-215">
              <h3 className="font-serif text-3xl mb-8 flex items-center gap-3" data-id="element-216">
                <AlertTriangle className="w-8 h-8 text-red-500" data-id="element-217" /> Who This Is
                NOT For
              </h3>
              <ul className="space-y-6" data-id="element-218">
                {["People looking for a long-term weight loss 'magic pill.'", 'Individuals with chronic kidney disease (due to mineral adjustments).'].map((text, i) => <li key={i} className="flex gap-4 text-neutral-400 text-lg" data-id="element-219">
                    <div className="w-2 h-2 rounded-full bg-red-500 mt-2.5 shrink-0" data-id="element-220" />
                    {text}
                  </li>)}
              </ul>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
                                11. FAQ — Objection handling (expanded)
                            ═══════════════════════════════════════════ */}
      <section className="py-24 bg-dark" data-id="element-221">
        <div className="container mx-auto px-6 max-w-3xl" data-id="element-222">
          <FadeIn data-id="element-223">
            <h2 className="font-serif text-4xl md:text-5xl text-center mb-16" data-id="element-224">
              Frequently Asked Questions
            </h2>
          </FadeIn>
          <div className="space-y-8" data-id="element-225">
            {[{
            q: 'Is this a laxative?',
            a: 'No. It uses minerals to balance water movement, not stimulants to irritate the colon.'
          }, {
            q: 'Will I be stuck in the bathroom?',
            a: "You will experience increased urination as the 'flush' occurs, but it is not an explosive-emergency method. Most people go about their day normally."
          }, {
            q: 'Can I drink coffee?',
            a: 'For 24 hours, we recommend swapping for ginger tea to allow the gut broom to reset. You can resume coffee the next day.'
          }, {
            q: 'Do I need special equipment?',
            a: 'No. Just basic ingredients found in any grocery store baking and produce aisle. Total cost is under $5.'
          }, {
            q: "What if it doesn't work for me?",
            a: "You're covered by our 60-day money-back guarantee. If you don't see results, email us for a full refund. No questions asked."
          }, {
            q: 'How quickly will I receive the method?',
            a: "Instantly. After checkout, you'll receive a PDF download link via email within 60 seconds. No waiting, no shipping."
          }].map((faq, i) => <FadeIn key={i} delay={i * 0.08} data-id="element-226">
                <div className="border-b border-neutral-800 pb-8" data-id="element-227">
                  <h4 className="text-xl font-bold text-white mb-3" data-id="element-228">{faq.q}</h4>
                  <p className="text-neutral-400 text-lg" data-id="element-229">{faq.a}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
                                12. GUARANTEE — Risk reversal
                            ═══════════════════════════════════════════ */}
      <section className="py-20 bg-neutral-950" data-id="element-230">
        <div className="container mx-auto px-6 max-w-4xl" data-id="element-231">
          <FadeIn data-id="element-232">
            <div className="border-2 border-cyan/30 bg-dark p-8 md:p-12 rounded-3xl text-center relative overflow-hidden shadow-[0_0_40px_rgba(0,229,204,0.1)]" data-id="element-233">
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-cyan via-yellow to-cyan" data-id="element-234" />
              <ShieldCheck className="w-20 h-20 text-cyan mx-auto mb-6" data-id="element-235" />
              <h2 className="font-serif text-3xl md:text-4xl text-white mb-6" data-id="element-236">
                60-Day 100% Money-Back Guarantee
              </h2>
              <p className="text-lg text-neutral-300 leading-relaxed max-w-2xl mx-auto" data-id="element-237">
                Try the entire 24-Hour Gut Flush Method risk-free. If you don't
                notice a visible difference in bloating within 24 hours of
                following the method, simply email us and we'll refund every
                penny. No questions asked. No hoops to jump through.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
                                13. FINAL OFFER — Countdown + CTA + After-purchase steps
                            ═══════════════════════════════════════════ */}
      <section className="py-24 relative overflow-hidden bg-dark" data-id="element-238">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl h-[500px] bg-yellow/5 blur-[150px] rounded-full pointer-events-none" data-id="element-239" />
        <div className="container mx-auto px-6 max-w-4xl relative z-10" data-id="element-240">
          <FadeIn data-id="element-241">
            <div id="checkout" className="bg-neutral-900 border border-neutral-800 p-8 md:p-16 rounded-3xl text-center shadow-2xl scroll-mt-8" data-id="element-242">
              <h2 className="font-serif text-4xl md:text-5xl mb-6 text-white" data-id="element-243">
                End the Discomfort Today
              </h2>
              <p className="text-xl text-neutral-300 mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-244">
                Get the full 24-Hour Gut Flush Method, the Hour-by-Hour Map, and
                the Mineral-Matrix Recipe today. Restore your profile and end
                the physical discomfort for less than the cost of a lunch you
                can't even enjoy right now.
              </p>

              <div className="mb-10" data-id="element-245">
                <div className="inline-block bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wider mb-8 animate-pulse" data-id="element-246">
                  🔥 Limited Time Offer
                </div>
                <CountdownTimer data-id="element-247" />
                <div className="flex items-center justify-center gap-4 mb-3" data-id="element-248">
                  <span className="text-3xl font-serif text-neutral-500 line-through" data-id="element-249">
                    $129
                  </span>
                  <span className="text-7xl font-serif text-white" data-id="element-250">
                    $12<span className="text-4xl" data-id="element-251">.99</span>
                  </span>
                </div>
                <p className="text-neutral-400 text-sm uppercase tracking-wider font-semibold mb-1" data-id="element-252">
                  Valued at $129 USD
                </p>
                <p className="text-red-500 font-bold text-lg mb-6" data-id="element-253">
                  🚨 Save Over 90%
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-neutral-400" data-id="element-254">
                  <span className="flex items-center gap-1.5 bg-neutral-800 px-3 py-1.5 rounded-full" data-id="element-255">
                    <Zap className="w-3.5 h-3.5 text-yellow" data-id="element-256" /> Instant Access
                  </span>
                  <span className="flex items-center gap-1.5 bg-neutral-800 px-3 py-1.5 rounded-full" data-id="element-257">
                    <ShieldCheck className="w-3.5 h-3.5 text-cyan" data-id="element-258" /> 100%
                    Secure Payment
                  </span>
                  <span className="flex items-center gap-1.5 bg-neutral-800 px-3 py-1.5 rounded-full" data-id="element-259">
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-400" data-id="element-260" />{' '}
                    Satisfaction Guaranteed
                  </span>
                </div>
              </div>

              <CTAButton href="https://www.foundationalgut.lat/cart/45550837170278:1" onClick={fireAddToCart} data-id="element-261" />

              <p className="mt-8 text-green-500 text-base md:text-lg italic leading-relaxed max-w-xl mx-auto mb-10" data-id="element-262">
                🌿 This costs less than 3 coffees — but unlike caffeine that
                wears off in under an hour, the knowledge you'll gain will serve
                you for a lifetime.
              </p>

              {/* After-purchase steps */}
              <div className="border-t border-neutral-800 pt-8 mt-4" data-id="element-263">
                <p className="text-neutral-500 uppercase tracking-widest text-xs font-bold mb-6" data-id="element-264">
                  After you click the button:
                </p>
                <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-sm text-neutral-400" data-id="element-265">
                  <span className="flex items-center gap-2" data-id="element-266">
                    <MousePointerClick className="w-4 h-4 text-cyan" data-id="element-267" /> Complete
                    secure checkout
                  </span>
                  <span className="hidden md:block text-neutral-700" data-id="element-268">→</span>
                  <span className="flex items-center gap-2" data-id="element-269">
                    <Mail className="w-4 h-4 text-cyan" data-id="element-270" /> PDF arrives in your
                    inbox instantly
                  </span>
                  <span className="hidden md:block text-neutral-700" data-id="element-271">→</span>
                  <span className="flex items-center gap-2" data-id="element-272">
                    <Sun className="w-4 h-4 text-cyan" data-id="element-273" /> Wake up lighter
                    tomorrow
                  </span>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
                                14. FOOTER
                            ═══════════════════════════════════════════ */}
      <footer className="border-t border-neutral-800 py-8 bg-neutral-950" data-id="element-274">
        <div className="container mx-auto px-6 max-w-6xl flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-275">
          <p className="text-neutral-500 text-sm" data-id="element-276">
            © {new Date().getFullYear()} The 24-Hour Gut Flush. All rights
            reserved.
          </p>
          <div className="flex items-center gap-6" data-id="element-277">
            <button onClick={() => setShowTerms(true)} className="text-neutral-500 hover:text-cyan text-sm transition-colors" data-id="element-278">
              Terms & Conditions
            </button>
            <button onClick={() => setShowPrivacy(true)} className="text-neutral-500 hover:text-cyan text-sm transition-colors" data-id="element-279">
              Privacy Policy
            </button>
          </div>
        </div>
      </footer>

      {/* LEGAL MODALS */}
      <LegalModal isOpen={showTerms} onClose={() => setShowTerms(false)} title="Terms & Conditions" data-id="element-280">
        <p data-id="element-281">
          <strong className="text-white" data-id="element-282">Last Updated:</strong> April 2026
        </p>
        <p data-id="element-283">
          By purchasing and using The 24-Hour Gut Flush method, you agree to the
          following terms and conditions.
        </p>
        <p data-id="element-284">
          <strong className="text-white" data-id="element-285">1. Product Description</strong>
        </p>
        <p data-id="element-286">
          The 24-Hour Gut Flush is a digital informational product delivered as
          a PDF. It provides a dietary and lifestyle method intended for general
          wellness purposes only. It is not a medical treatment, prescription,
          or diagnosis.
        </p>
        <p data-id="element-287">
          <strong className="text-white" data-id="element-288">2. Medical Disclaimer</strong>
        </p>
        <p data-id="element-289">
          This product is for informational purposes only and is not intended to
          replace professional medical advice, diagnosis, or treatment. Always
          consult your physician or qualified healthcare provider before
          starting any new health method, especially if you have pre-existing
          conditions such as kidney disease, heart conditions, or electrolyte
          imbalances.
        </p>
        <p data-id="element-290">
          <strong className="text-white" data-id="element-291">3. No Guarantees</strong>
        </p>
        <p data-id="element-292">
          Results may vary from person to person. While the method is based on
          established physiological principles, individual outcomes depend on
          numerous factors including current health status, adherence to the
          method, and individual biology. We do not guarantee specific results.
        </p>
        <p data-id="element-293">
          <strong className="text-white" data-id="element-294">4. Digital Delivery</strong>
        </p>
        <p data-id="element-295">
          Upon purchase, you will receive immediate access to the digital
          product via PDF download. Due to the digital nature of this product,
          all sales are final unless covered by our 60-day guarantee.
        </p>
        <p data-id="element-296">
          <strong className="text-white" data-id="element-297">5. Intellectual Property</strong>
        </p>
        <p data-id="element-298">
          All content within The 24-Hour Gut Flush is protected by copyright.
          You may not reproduce, distribute, or share the content without
          written permission.
        </p>
        <p data-id="element-299">
          <strong className="text-white" data-id="element-300">6. Limitation of Liability</strong>
        </p>
        <p data-id="element-301">
          The creators and distributors of this product shall not be held liable
          for any adverse effects, damages, or consequences resulting from the
          use or misuse of the information provided.
        </p>
      </LegalModal>

      <LegalModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} title="Privacy Policy" data-id="element-302">
        <p data-id="element-303">
          <strong className="text-white" data-id="element-304">Last Updated:</strong> April 2026
        </p>
        <p data-id="element-305">
          Your privacy is important to us. This policy outlines how we collect,
          use, and protect your information.
        </p>
        <p data-id="element-306">
          <strong className="text-white" data-id="element-307">1. Information We Collect</strong>
        </p>
        <p data-id="element-308">
          When you purchase The 24-Hour Gut Flush, we collect only the
          information necessary to process your transaction: your name, email
          address, and payment details. Payment processing is handled by secure
          third-party processors — we never store your credit card information.
        </p>
        <p data-id="element-309">
          <strong className="text-white" data-id="element-310">2. How We Use Your Information</strong>
        </p>
        <p data-id="element-311">
          Your information is used solely to deliver the purchased product, send
          order confirmations, and provide customer support if needed. We do not
          sell, rent, or share your personal information with third parties for
          marketing purposes.
        </p>
        <p data-id="element-312">
          <strong className="text-white" data-id="element-313">3. Cookies</strong>
        </p>
        <p data-id="element-314">
          Our website may use essential cookies to ensure proper functionality
          and analytics cookies to understand how visitors interact with our
          pages. You can disable cookies in your browser settings at any time.
        </p>
        <p data-id="element-315">
          <strong className="text-white" data-id="element-316">4. Data Security</strong>
        </p>
        <p data-id="element-317">
          We implement industry-standard security measures including SSL
          encryption to protect your personal information during transmission
          and storage.
        </p>
        <p data-id="element-318">
          <strong className="text-white" data-id="element-319">5. Data Retention</strong>
        </p>
        <p data-id="element-320">
          We retain your purchase information for as long as necessary to
          fulfill our obligations and comply with legal requirements. You may
          request deletion of your data at any time.
        </p>
        <p data-id="element-321">
          <strong className="text-white" data-id="element-322">6. Your Rights</strong>
        </p>
        <p data-id="element-323">
          You have the right to access, correct, or delete your personal data.
          To exercise these rights, please reach out through our support
          channels.
        </p>
        <p data-id="element-324">
          <strong className="text-white" data-id="element-325">7. Changes to This Policy</strong>
        </p>
        <p data-id="element-326">
          We may update this privacy policy from time to time. Any changes will
          be reflected on this page with an updated revision date.
        </p>
      </LegalModal>
    </div>;
}